import React, { useState, useRef } from 'react';
import { BranchSummaryItem } from '../../hooks/useBrandQueries';
import ChartControls from '../ui/ChartControls';
import LoadingSpinner from '../ui/LoadingSpinner';
import EmptyState from '../ui/EmptyState';
import HorizontalTableShell from '../ui/HorizontalTableShell';
import { logger } from '../../utils/logger';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface AdvancedComparisonTabProps {
  branches: BranchSummaryItem[];
  allMetrics: Array<{ id: string; label: string; unit: string; category: string }>;
  loading?: boolean;
}

/**
 * Advanced Comparison Tab Component
 * 
 * Displays detailed side-by-side comparison of branches across all metrics
 * Similar to mobile comparison on e-commerce sites
 */
const AdvancedComparisonTab: React.FC<AdvancedComparisonTabProps> = ({
  branches,
  allMetrics,
  loading = false
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const [selectedBranches, setSelectedBranches] = useState<number[]>(
    branches.slice(0, 3).map(b => b.branchId)
  );
  
  const componentId = 'branch-comparison-advanced';
  const componentTitle = 'مقایسه پیشرفته شعب';

  // Update selected branches when branches prop changes
  React.useEffect(() => {
    if (branches.length > 0 && selectedBranches.length === 0) {
      setSelectedBranches(branches.slice(0, 3).map(b => b.branchId));
    }
  }, [branches, selectedBranches.length]);

  if (loading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-1">
        <div className="flex flex-col items-center justify-center h-64">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری مقایسه پیشرفته…"
          />
        </div>
      </div>
    );
  }

  if (!branches || branches.length === 0) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-1">
        <EmptyState message="داده‌ای برای نمایش موجود نیست" />
      </div>
    );
  }

  // Filter selected branches
  const displayBranches = branches.filter(b => selectedBranches.includes(b.branchId));
  
  // Map BranchSummaryItem to metrics object for display
  const branchesWithMetrics = displayBranches.map(branch => {
    try {
      return {
        branchId: branch.branchId,
        branchName: branch.branchName,
        currency: branch.currency,
        metrics: {
          revenue: branch.currentMonthRevenue,
          customerCount: branch.customerCount,
          avgTransactionValue: branch.avgTransactionValue,
          satisfactionScore: branch.satisfactionScore,
          clubMembershipRate: branch.clubMembershipRate,
          performanceScore: branch.performanceScore,
          // Add other metrics as they become available from API
        }
      };
    } catch (error) {
      logger.error('Failed to map branch data', 'AdvancedComparisonTab', { branch, error });
      return {
        branchId: branch.branchId,
        branchName: branch.branchName,
        currency: branch.currency,
        metrics: {}
      };
    }
  });

  // Group metrics by category
  const groupedMetrics = allMetrics.reduce((acc, metric) => {
    if (!acc[metric.category]) {
      acc[metric.category] = [];
    }
    acc[metric.category].push(metric);
    return acc;
  }, {} as Record<string, typeof allMetrics>);

  const categoryLabels: Record<string, string> = {
    financial: 'مالی',
    customer: 'مشتری',
    performance: 'عملکرد'
  };

  // Format value based on unit
  const formatValue = (value: number, unit: string, currency?: string) => {
    if (!value && value !== 0) return '-';
    
    const formatted = value.toLocaleString('fa-IR');
    
    switch (unit) {
      case 'percentage':
        return `${formatted}%`;
      case 'currency':
        return `${formatted} ${currency || 'تومان'}`;
      case 'score':
        return `${formatted}/۱۰۰`;
      default:
        return formatted;
    }
  };

  // Get best value for highlighting
  const getBestBranch = (metric: { id: string; unit: string }) => {
    try {
      const values = branchesWithMetrics.map(b => ({
        branchId: b.branchId,
        value: b.metrics[metric.id] || 0
      }));
      
      const maxValue = Math.max(...values.map(v => v.value));
      return values.find(v => v.value === maxValue)?.branchId;
    } catch (error) {
      logger.warn('Failed to get best branch', 'AdvancedComparisonTab', { metric, error });
      return undefined;
    }
  };

  return (
    <div className="space-y-6 animate-enter-up">
      {/* Branch Selector */}
      <div className="card-base card-padding animate-enter">
        <div className="flex items-center justify-between mb-4">
          <h3 className="title-card text-text-primary">
            انتخاب شعب برای مقایسه
          </h3>
          <span className="text-sm text-text-secondary">
            حداکثر 3 شعبه
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {branches.map(branch => (
            <button
              key={branch.branchId}
              onClick={() => {
                if (selectedBranches.includes(branch.branchId)) {
                  setSelectedBranches(selectedBranches.filter(id => id !== branch.branchId));
                } else if (selectedBranches.length < 3) {
                  setSelectedBranches([...selectedBranches, branch.branchId]);
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-base min-h-[44px] touch-manipulation ${
                selectedBranches.includes(branch.branchId)
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-bg-secondary text-text-primary hover:bg-bg-tertiary border border-border-default'
              } ${!selectedBranches.includes(branch.branchId) && selectedBranches.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!selectedBranches.includes(branch.branchId) && selectedBranches.length >= 3}
              aria-pressed={selectedBranches.includes(branch.branchId)}
            >
              {branch.branchName}
            </button>
          ))}
        </div>
      </div>

      {/* Comparison Table */}
      <div ref={tableRef} className="card-base overflow-hidden animate-enter animate-delay-2">
        <div className="card-padding pb-0">
          <ChartControls
            componentId={componentId}
            componentTitle={componentTitle}
            componentType="table"
            dashboardSource="branchComparison"
            onInfo={() => {
              console.log('Advanced comparison table info');
            }}
            onFavorite={() => handleFavoriteToggle({
              componentId,
              componentTitle,
              dashboardSource: 'branchComparison',
              componentType: 'table',
              description: 'جدول مقایسه پیشرفته تمام معیارها بین شعب',
              props: { selectedBranches },
              gridLayout: {
                containerClass: 'grid grid-cols-1 gap-6'
              }
            })}
            isFavorited={isFavorited(componentId)}
            chartRef={tableRef}
            className="mb-4"
          />
        </div>
        <HorizontalTableShell
          minWidth={600 + branchesWithMetrics.length * 140}
          panelClassName="border-0 rounded-none"
        >
          <table className="w-full">
            <thead className="bg-bg-secondary sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary border-b border-border-default">
                  معیار
                </th>
                {branchesWithMetrics.map(branch => (
                  <th
                    key={branch.branchId}
                    className="px-4 py-3 text-center text-sm font-semibold text-text-primary border-b border-border-default"
                  >
                    {branch.branchName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedMetrics).map(([category, metrics]) => (
                <React.Fragment key={category}>
                  {/* Category Header */}
                  <tr className="bg-bg-tertiary">
                    <td
                      colSpan={branchesWithMetrics.length + 1}
                      className="px-4 py-2 text-sm font-semibold text-text-primary"
                    >
                      {categoryLabels[category] || category}
                    </td>
                  </tr>
                  
                  {/* Metrics in this category */}
                  {metrics.map(metric => {
                    const bestBranchId = getBestBranch(metric);
                    
                    return (
                      <tr key={metric.id} className="border-b border-border-default hover:bg-bg-secondary transition-colors">
                        <td className="px-4 py-3 text-sm text-text-secondary">
                          {metric.label}
                        </td>
                        {branchesWithMetrics.map(branch => {
                          const value = branch.metrics[metric.id];
                          const isBest = branch.branchId === bestBranchId && value > 0;
                          
                          return (
                            <td
                              key={branch.branchId}
                              className={`px-4 py-3 text-center text-sm font-medium transition-colors ${
                                isBest
                                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                  : 'text-text-primary'
                              }`}
                            >
                              <div className="flex items-center justify-center gap-1">
                                {isBest && <span className="text-sm">🏆</span>}
                                <span>{formatValue(value, metric.unit, branch.currency)}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </HorizontalTableShell>
      </div>

      {/* Legend */}
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex items-center gap-6 text-sm text-text-secondary flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-lg">🏆</span>
            <span>بهترین عملکرد</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded"></div>
            <span>برجسته شده</span>
          </div>
          <div className="flex items-center gap-2 mr-auto">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">مقادیر بر اساس آخرین ماه محاسبه شده‌اند</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedComparisonTab;
